import type { SyncEntity } from '@bssb/contracts';
import { sealField, sealOptional } from './crypto.ts';
import type {
  CachedAddress,
  CachedCollateral,
  CachedContact,
  CachedCustomer,
  CachedLoan,
  CachedPaymentHistory,
  CachedVisit,
} from './db.ts';
import { db } from './db.ts';
import { buildSearchTokens } from './search-tokens.ts';

/**
 * Penerapan hasil `sync/pull` ke cache lokal (T-P5-14).
 *
 * Data master dari server selalu menang mutlak (§7.2): baris lokal ditimpa,
 * tidak digabung. Nilai pribadi dienkripsi di sini — satu-satunya tempat data
 * masuk ke Dexie — sehingga tidak ada jalur yang bisa lupa menyandikannya.
 */

type Row = Record<string, unknown>;

const str = (row: Row, key: string): string => String(row[key] ?? '');
const nullable = (row: Row, key: string): string | null =>
  row[key] === null || row[key] === undefined ? null : String(row[key]);
const num = (row: Row, key: string): number | null =>
  row[key] === null || row[key] === undefined ? null : Number(row[key]);

export async function applyPull(entity: SyncEntity, items: Row[]): Promise<void> {
  if (items.length === 0) return;

  switch (entity) {
    case 'customers':
      await db.customers.bulkPut(await Promise.all(items.map(toCustomer)));
      return;
    case 'loans':
      await db.loans.bulkPut(await Promise.all(items.map(toLoan)));
      return;
    case 'addresses':
      await db.addresses.bulkPut(await Promise.all(items.map(toAddress)));
      return;
    case 'contacts':
      await db.contacts.bulkPut(await Promise.all(items.map(toContact)));
      return;
    case 'collaterals':
      await db.collaterals.bulkPut(items.map(toCollateral));
      return;
    case 'paymentHistory':
      await db.paymentHistory.bulkPut(items.map(toPaymentHistory));
      return;
    case 'visitHistory':
      await db.visitHistory.bulkPut(items.map(toVisit));
      return;
  }
}

async function toCustomer(row: Row): Promise<CachedCustomer> {
  const fullName = str(row, 'fullName');
  const phonePrimary = nullable(row, 'phonePrimary');

  return {
    id: str(row, 'id'),
    branchId: str(row, 'branchId'),
    cif: str(row, 'cif'),
    fullName: await sealField(fullName),
    nikLast4: nullable(row, 'nikLast4'),
    birthPlace: nullable(row, 'birthPlace'),
    birthDate: nullable(row, 'birthDate'),
    gender: nullable(row, 'gender'),
    occupation: nullable(row, 'occupation'),
    phonePrimary: await sealOptional(phonePrimary),
    phoneAlt: await sealOptional(nullable(row, 'phoneAlt')),
    searchTokens: await buildSearchTokens([
      fullName,
      phonePrimary,
      nullable(row, 'phoneAlt'),
      str(row, 'cif'),
    ]),
    updatedAt: str(row, 'updatedAt'),
  };
}

async function toLoan(row: Row): Promise<CachedLoan> {
  return {
    id: str(row, 'id'),
    customerId: str(row, 'customerId'),
    branchId: str(row, 'branchId'),
    loanNumber: str(row, 'loanNumber'),
    productName: nullable(row, 'productName'),
    plafon: str(row, 'plafon'),
    outstanding: str(row, 'outstanding'),
    arrearsPrincipal: str(row, 'arrearsPrincipal'),
    arrearsInterest: str(row, 'arrearsInterest'),
    penalty: str(row, 'penalty'),
    installmentAmount: str(row, 'installmentAmount'),
    nextDueDate: nullable(row, 'nextDueDate'),
    oldestUnpaidDueDate: nullable(row, 'oldestUnpaidDueDate'),
    dpd: Number(row.dpd ?? 0),
    collectibility: Number(row.collectibility ?? 1),
    status: str(row, 'status'),
    // Nomor pinjaman tidak rahasia, tetapi tetap dijadikan token supaya
    // pencarian nama dan nomor melewati jalur yang sama.
    searchTokens: await buildSearchTokens([str(row, 'loanNumber')]),
    updatedAt: str(row, 'updatedAt'),
  };
}

async function toAddress(row: Row): Promise<CachedAddress> {
  return {
    id: str(row, 'id'),
    customerId: str(row, 'customerId'),
    type: str(row, 'type'),
    line1: await sealField(str(row, 'line1')),
    rtRw: nullable(row, 'rtRw'),
    kelurahan: nullable(row, 'kelurahan'),
    kecamatan: nullable(row, 'kecamatan'),
    city: nullable(row, 'city'),
    province: nullable(row, 'province'),
    postalCode: nullable(row, 'postalCode'),
    latitude: num(row, 'latitude'),
    longitude: num(row, 'longitude'),
    geoSource: nullable(row, 'geoSource'),
    geoUpdatedAt: nullable(row, 'geoUpdatedAt'),
    isPrimary: Boolean(row.isPrimary),
    updatedAt: str(row, 'updatedAt'),
  };
}

async function toContact(row: Row): Promise<CachedContact> {
  return {
    id: str(row, 'id'),
    customerId: str(row, 'customerId'),
    relation: str(row, 'relation'),
    name: await sealField(str(row, 'name')),
    phone: await sealOptional(nullable(row, 'phone')),
    address: await sealOptional(nullable(row, 'address')),
  };
}

function toCollateral(row: Row): CachedCollateral {
  return {
    id: str(row, 'id'),
    loanId: str(row, 'loanId'),
    type: str(row, 'type'),
    description: nullable(row, 'description'),
    documentNo: nullable(row, 'documentNo'),
    appraisedValue: nullable(row, 'appraisedValue'),
    appraisedAt: nullable(row, 'appraisedAt'),
    locationText: nullable(row, 'locationText'),
    latitude: num(row, 'latitude'),
    longitude: num(row, 'longitude'),
  };
}

function toPaymentHistory(row: Row): CachedPaymentHistory {
  return {
    id: str(row, 'id'),
    loanId: str(row, 'loanId'),
    externalRef: str(row, 'externalRef'),
    paidAt: str(row, 'paidAt'),
    amount: str(row, 'amount'),
    allocPrincipal: str(row, 'allocPrincipal'),
    allocInterest: str(row, 'allocInterest'),
    allocPenalty: str(row, 'allocPenalty'),
    channel: nullable(row, 'channel'),
  };
}

function toVisit(row: Row): CachedVisit {
  return {
    id: str(row, 'id'),
    clientUuid: str(row, 'clientUuid'),
    loanId: str(row, 'loanId'),
    customerId: str(row, 'customerId'),
    collectorId: str(row, 'collectorId'),
    checkinAt: str(row, 'checkinAt'),
    checkoutAt: nullable(row, 'checkoutAt'),
    result: str(row, 'result'),
    resultSubReason: nullable(row, 'resultSubReason'),
    notes: nullable(row, 'notes'),
    isFlagged: Boolean(row.isFlagged),
    flagReason: nullable(row, 'flagReason'),
    distanceToCustomerM: num(row, 'distanceToCustomerM'),
    serverReceivedAt: str(row, 'serverReceivedAt'),
    pending: false,
  };
}

/**
 * Pangkas data yang tidak lagi menjadi tanggung jawab perangkat (BR-11).
 *
 * Pinjaman yang lunas atau berpindah collector membawa serta alamat, kontak,
 * agunan, dan riwayatnya: menyisakan potongannya berarti nama dan alamat
 * nasabah tetap tersimpan di perangkat yang sudah tidak berhak melihatnya.
 */
export async function pruneRemoved(entity: SyncEntity, ids: string[]): Promise<number> {
  if (ids.length === 0) return 0;

  if (entity === 'loans') {
    const customerIds = new Set<string>();
    for (const id of ids) {
      const loan = await db.loans.get(id);
      if (loan) customerIds.add(loan.customerId);
    }

    await db.loans.bulkDelete(ids);
    await db.collaterals.where('loanId').anyOf(ids).delete();
    await db.paymentHistory.where('loanId').anyOf(ids).delete();
    await db.visitHistory.where('loanId').anyOf(ids).delete();

    // Nasabah dipertahankan selama masih punya pinjaman lain di perangkat ini.
    for (const customerId of customerIds) {
      const remaining = await db.loans.where('customerId').equals(customerId).count();
      if (remaining === 0) await removeCustomer(customerId);
    }

    return ids.length;
  }

  if (entity === 'customers') {
    for (const id of ids) await removeCustomer(id);
    return ids.length;
  }

  await db.table(entity).bulkDelete(ids);
  return ids.length;
}

async function removeCustomer(customerId: string): Promise<void> {
  await db.customers.delete(customerId);
  await db.addresses.where('customerId').equals(customerId).delete();
  await db.contacts.where('customerId').equals(customerId).delete();
}
